<template>
    <vx-radio-selection v-model="selected" :disabled="props.disabled">
        <vx-radio-selection-item
            v-for="option in statusOptions"
            :key="option.value"
            :value="option.value"
            :disabled="isOptionDisabled(option.value)"
            :data-test="`group-status-option-${option.value}`"
        >
            <template v-slot:header>
                <span class="capitalize">{{ option.value }}</span>
            </template>
            <template v-slot:content>
                <div>{{ option.description }}</div>
                <div
                    v-if="option.value === 'closed' && closedBlockedByBilling"
                    class="mt-2 text-xs text-rose-800"
                    data-test="closed-billing-block"
                >
                    Groups with active billing cannot be closed. Billing must be disabled first.
                </div>
            </template>
        </vx-radio-selection-item>
    </vx-radio-selection>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { VxRadioSelection, VxRadioSelectionItem } from '@voxie/frontend-components';
import {
    GROUP_STATUS_ACTIVE,
    GROUP_STATUS_ALLOWED_TRANSITIONS,
    GROUP_STATUS_CLOSED,
    GROUP_STATUS_DRAFT,
    GROUP_STATUS_SUSPENDED,
    type GroupStatus,
} from '~/constants/groupStatuses';

const props = withDefaults(
    defineProps<{
        currentStatus: GroupStatus;
        isBillable: boolean;
        disabled?: boolean;
    }>(),
    {
        disabled: false,
    }
);

const selected = defineModel<GroupStatus>({ required: true });

const statusOptions: { value: GroupStatus; description: string }[] = [
    {
        value: GROUP_STATUS_DRAFT,
        description: 'Group is not yet operational. Assign phone numbers and activate when ready.',
    },
    {
        value: GROUP_STATUS_ACTIVE,
        description: 'Group is operational and can send messages.',
    },
    {
        value: GROUP_STATUS_SUSPENDED,
        description: 'Group is temporarily paused. Activity is halted until reactivated.',
    },
    {
        value: GROUP_STATUS_CLOSED,
        description: 'Group is permanently closed. Phone numbers are released and cannot be recovered.',
    },
];

const closedBlockedByBilling = computed(() => props.isBillable);

const isOptionDisabled = (target: GroupStatus) => {
    if (target === props.currentStatus) {
        return false;
    }
    if (target === GROUP_STATUS_CLOSED && closedBlockedByBilling.value) {
        return true;
    }
    const allowed = GROUP_STATUS_ALLOWED_TRANSITIONS[props.currentStatus] ?? [];
    return !allowed.includes(target);
};
</script>
